import {Component, OnInit} from '@angular/core';
import {AuthService} from '../../../services/auth/auth.service';
import {Partner} from '../../../models/partner';
import {FormGroup, FormsModule, ReactiveFormsModule} from '@angular/forms';
import {forkJoin} from 'rxjs';
import {Country} from '../../../models/country';
import {LangService} from '../../../services/language/lang.service';
import {TranslatePipe} from '@ngx-translate/core';
import {NgClass, NgForOf, NgIf, NgOptimizedImage} from '@angular/common';
import {DropdownSelectComponent} from '../../layout/dropdown-select/dropdown-select.component';
import {DropdownSelectOptionComponent} from '../../layout/dropdown-select-option/dropdown-select-option.component';
import {PartnerForm} from '../../../forms/partner-form';
import {ContactForm} from '../../../forms/contact-form';
import {PartnerService} from '../../../services/partner/partner.service';
import {PartnerContactService} from '../../../services/partner/partner-contact.service';
import {SnackbarService} from '../../../services/snackbar/snackbar.service';
import {UploadFile} from '../../../models/upload-file';
import {UploadFileService} from '../../../services/upload-file/upload-file.service';
import {MetaTitle} from '../../../decorator/meta-title';
import {LoaderService} from '../../../services/loader/loader.service';
import {ElementLoaderDirective} from '../../../directives/element-loader/element-loader.directive';
import {ComplianceDocumentType} from '../../../constants/compliance-document-type';
import {PartnerComplianceDocumentService} from '../../../services/partner/partner-compliance-document.service';
import {ComplianceUploadFile} from '../../../models/compliance-upload-file';

@Component({
  selector: 'app-my-account',
  imports: [
    TranslatePipe,
    NgForOf,
    FormsModule,
    ReactiveFormsModule,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    DropdownSelectComponent,
    DropdownSelectOptionComponent,
    FormsModule,
    NgForOf,
    NgOptimizedImage,
    ReactiveFormsModule,
    FormsModule,
    NgForOf,
    ReactiveFormsModule,
    FormsModule,
    NgForOf,
    ReactiveFormsModule,
    TranslatePipe,
    NgIf,
    NgIf,
    ElementLoaderDirective,
    NgClass,
    NgIf
  ],
  templateUrl: './my-account.component.html',
  styleUrl: './my-account.component.scss'
})
@MetaTitle('My account')
export class MyAccountComponent implements OnInit {

  connectedUser: Partner | null = null;

  loading: boolean = true;
  submittingCompany: boolean = false;
  submittingPayment: boolean = false;
  submittingMainContact: boolean = false;
  submittingDocuments: boolean = false;

  langs: any[] = [];
  countries: Country[] = [];
  civilityList: string[] = [];
  companyStatusList: string[] = [];

  form: {
    company: FormGroup,
    mainContact: FormGroup,
    payment: FormGroup,
  } = {
    company: PartnerForm.getCompany(),
    mainContact: ContactForm.getFormGroup(),
    payment: PartnerForm.getPayment(),
  }

  mainContactDocument: UploadFile | null = null;

  documentTypeList: ComplianceDocumentType[] = [
    ComplianceDocumentType.PROOF_OF_IDENTITY,
    ComplianceDocumentType.CERTIFICATE_OF_INCORPORATION,
    ComplianceDocumentType.SIGNED_CONTRACT,
    ComplianceDocumentType.INSERTION_ORDER,
  ];

  upload: Record<ComplianceDocumentType, ComplianceUploadFile> = {
    [ComplianceDocumentType.PROOF_OF_IDENTITY]: new ComplianceUploadFile(),
    [ComplianceDocumentType.CERTIFICATE_OF_INCORPORATION]: new ComplianceUploadFile(),
    [ComplianceDocumentType.SIGNED_CONTRACT]: new ComplianceUploadFile(),
    [ComplianceDocumentType.INSERTION_ORDER]: new ComplianceUploadFile(),
  };

  constructor(
    protected langService: LangService,
    private auth: AuthService,
    private loader: LoaderService,
    private snackbar: SnackbarService,
    private partnerService: PartnerService,
    private uploadFileService: UploadFileService,
    private partnerContactService: PartnerContactService,
    private complianceDocumentService: PartnerComplianceDocumentService,
  ) {
  }

  ngOnInit(): void {
    this.loader.startLoading();

    forkJoin([
      this.auth.getConnectedUser(),
      this.auth.getAsyncConfig()
    ]).subscribe(([connectedUser, config]) => {
      if (connectedUser) {
        PartnerForm.updateCompany(this.form.company, connectedUser);
        PartnerForm.updatePayment(this.form.payment, connectedUser);
        ContactForm.update(this.form.mainContact, connectedUser.getMainContact());

        this.initConnectedUser(connectedUser);

        this.countries = config?.countries ?? [];
        this.civilityList = config?.data?.civilityList;
        this.companyStatusList = config?.data?.companyStatusList;
        this.langs = this.langService.getPartnerLangs();

        this.loading = false;
        this.loader.stopLoading();
      }
    });
  }

  onSubmitCompany(): void {
    const partner = this.connectedUser;
    if (!partner || !partner.id) {
      return;
    }
    this.submittingCompany = true;

    this.partnerService.update(partner.id, this.form.company.value).subscribe(() => {
      this.submittingCompany = false;
      this.snackbar.success('Company information updated.');
    });
  }

  onSubmitPayment(): void {
    const partner = this.connectedUser;
    if (!partner || !partner.id) {
      return;
    }
    this.submittingPayment = true;

    this.partnerService.update(partner.id, this.form.payment.value).subscribe(() => {
      this.submittingPayment = false;
      this.snackbar.success('Payment information updated.');
    });
  }

  onSubmitContact(): void {
    const partner = this.connectedUser;
    if (!partner) {
      return;
    }

    const contact = partner.getMainContact()
    if (!contact.id) {
      return;
    }

    this.submittingMainContact = true;

    const params: any = this.form.mainContact.value;
    if (this.mainContactDocument) {
      params.document = this.mainContactDocument;
    }

    this.partnerContactService.update(contact.id, params).subscribe(() => {
      this.auth.reloadConnectedUser().subscribe((connectedUser: Partner | null) => {
        this.initConnectedUser(connectedUser);
        this.submittingMainContact = false;

        this.snackbar.success('Main contact information updated.');
      });
    });
  }

  submitDocuments(): void {
    this.submittingDocuments = true;
    this?.sendUploadedFile();
  }

  sendUploadedFile(index: number = 0): void {
    const type = this.documentTypeList[index];
    if (!type) {
      // We reached the end of the list

      if (index > 0) {
        this.auth.reloadConnectedUser().subscribe((connectedUser: Partner | null) => {
          this.initConnectedUser(connectedUser);
          this.submittingDocuments = false;

          this.snackbar.success('Your documents have been successfully updated.');
        });
      }

      return;
    }

    const complianceUpload: ComplianceUploadFile = this.upload[type];
    if (complianceUpload.isEmpty()) {
      return this.sendUploadedFile(index + 1);
    }

    const data = complianceUpload.getData(type)

    if (complianceUpload.id) {
      this.complianceDocumentService.update(complianceUpload.id, data).subscribe(() => {
        this.sendUploadedFile(index + 1);
      });
    } else {
      this.complianceDocumentService.create(data).subscribe(() => {
        this.sendUploadedFile(index + 1);
      });
    }
  }

  imageUpload(event: Event): void {
    const target: any = event.target;
    const file = target?.files[0];

    this.uploadFileService.upload(file).subscribe((uploadFile: UploadFile) => {
      this.mainContactDocument = uploadFile;
    });
  }

  documentUpload(event: Event, complianceDocumentType: ComplianceDocumentType): void {
    const target: any = event.target;
    const file = target?.files[0];

    this.uploadFileService.upload(file).subscribe((uploadFile: UploadFile) => {
      this.upload[complianceDocumentType].document = uploadFile;
    });
  }

  getDocumentTypeIcon(documentType: ComplianceDocumentType): string {
    switch (documentType) {
      case ComplianceDocumentType.PROOF_OF_IDENTITY:
        return 'bi-person-badge-fill';
      case ComplianceDocumentType.CERTIFICATE_OF_INCORPORATION:
        return 'bi-building';
      case ComplianceDocumentType.SIGNED_CONTRACT:
        return 'bi-lightbulb-fill';
      case ComplianceDocumentType.INSERTION_ORDER:
        return 'bi-journal-text';
    }
  }

  private initConnectedUser(connectedUser: Partner | null): void {
    this.connectedUser = connectedUser;

    for (const [type, document] of Object.entries(connectedUser?.documents ?? [])) {
      this.upload[type as ComplianceDocumentType] = new ComplianceUploadFile().deserialize({
        id: document.id ?? null
      });
    }
  }
}
